import React, { useEffect, useState } from 'react';
import { X, Share2, Trash2, Globe, User as UserIcon } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';

// Reusable across Lead/Deal/Account/Contact/Task detail views — manages
// RecordShare grants for a single record (src/models/RecordShare.js,
// src/utils/recordVisibility.js on the backend). Sharing only actually
// changes what a non-admin sees once the record's own visibility is set to
// 'private' — on a 'team' record every company member can already see it.
export default function ShareModal({ isOpen, onClose, resourceType, resourceId, resourceLabel }) {
  const [shares, setShares] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [permission, setPermission] = useState('read');

  useEffect(() => {
    if (!isOpen || !resourceId) return;
    setLoading(true);
    Promise.all([
      api.get('/crm/shares', { params: { resourceType, resourceId } }),
      api.get('/auth/team-members'),
    ])
      .then(([sharesRes, usersRes]) => {
        setShares(sharesRes.data.shares || []);
        setTeamMembers(usersRes.data.users || []);
      })
      .catch(() => alertService.error('Error', 'Failed to load sharing details'))
      .finally(() => setLoading(false));
  }, [isOpen, resourceType, resourceId]);

  if (!isOpen) return null;

  const refreshShares = async () => {
    const res = await api.get('/crm/shares', { params: { resourceType, resourceId } });
    setShares(res.data.shares || []);
  };

  const handleAddUserShare = async () => {
    if (!selectedUserId) return alertService.error('Error', 'Select a teammate first');
    setSaving(true);
    try {
      await api.post('/crm/shares', {
        resourceType, resourceId, sharedWithType: 'user', sharedWithId: selectedUserId, permission,
      });
      await refreshShares();
      setSelectedUserId('');
      alertService.success('Shared', 'Teammate can now access this record');
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to share record');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPublicShare = async (sharedWithType) => {
    setSaving(true);
    try {
      await api.post('/crm/shares', { resourceType, resourceId, sharedWithType, permission });
      await refreshShares();
      alertService.success('Shared', 'Every team member can now access this record');
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to share record');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (shareId) => {
    const confirmed = await alertService.confirm('Remove Access?', 'This grant will be revoked immediately.', 'warning', 'Remove');
    if (!confirmed) return;
    try {
      await api.delete(`/crm/shares/${shareId}`);
      await refreshShares();
    } catch {
      alertService.error('Error', 'Failed to remove share');
    }
  };

  const userLabel = (id) => {
    const u = teamMembers.find(m => m._id === id);
    return u ? `${[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}` : id;
  };

  const publicShare = shares.find(s => s.sharedWithType === 'public_read' || s.sharedWithType === 'public_read_write');
  const userShares = shares.filter(s => s.sharedWithType === 'user');
  const availableUsers = teamMembers.filter(u => !userShares.some(s => s.sharedWithId === u._id));

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E] flex items-center gap-2">
            <Share2 size={16} className="text-[#0176D3]" /> Share {resourceLabel || 'Record'}
          </h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="text-[13px] text-[#706E6B]">Loading sharing details…</p>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Everyone in the company</label>
                {publicShare ? (
                  <div className="flex items-center justify-between p-2.5 bg-[#F3F2F2] border border-[#D8DDE6] rounded">
                    <span className="flex items-center gap-2 text-[13px] font-medium text-[#080707]">
                      <Globe size={14} className="text-[#0176D3]" />
                      Public — {publicShare.sharedWithType === 'public_read_write' ? 'can view & edit' : 'can view'}
                    </span>
                    <button onClick={() => handleRemove(publicShare._id)} className="text-[#706E6B] hover:text-[#EA001E]"><Trash2 size={14} /></button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleAddPublicShare('public_read')}
                    className="w-full h-9 flex items-center justify-center gap-2 border border-dashed border-[#D8DDE6] rounded text-[13px] font-bold text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3] disabled:opacity-50"
                  >
                    <Globe size={14} /> Make visible to everyone
                  </button>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Shared with</label>
                {userShares.length === 0 && <p className="text-[12px] text-[#706E6B]">No individual teammates have been granted access yet.</p>}
                <div className="space-y-1.5">
                  {userShares.map(s => (
                    <div key={s._id} className="flex items-center justify-between p-2.5 bg-[#F3F2F2] border border-[#D8DDE6] rounded">
                      <span className="flex items-center gap-2 text-[13px] font-medium text-[#080707]">
                        <UserIcon size={14} className="text-[#706E6B]" /> {userLabel(s.sharedWithId)}
                        <span className="text-[11px] font-bold text-[#706E6B] uppercase">{s.permission}</span>
                      </span>
                      <button onClick={() => handleRemove(s._id)} className="text-[#706E6B] hover:text-[#EA001E]"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-[#D8DDE6]">
                <label className="text-[11px] font-semibold text-[#706E6B]">Add a teammate</label>
                <div className="flex gap-2">
                  <select
                    value={selectedUserId}
                    onChange={e => setSelectedUserId(e.target.value)}
                    className="flex-1 h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                  >
                    <option value="">Select teammate…</option>
                    {availableUsers.map(u => (
                      <option key={u._id} value={u._id}>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</option>
                    ))}
                  </select>
                  <select
                    value={permission}
                    onChange={e => setPermission(e.target.value)}
                    className="w-28 h-9 px-2 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                  >
                    <option value="read">Read</option>
                    <option value="edit">Edit</option>
                    <option value="delete">Delete</option>
                    <option value="transfer">Transfer</option>
                  </select>
                </div>
                <button
                  type="button"
                  disabled={saving || !selectedUserId}
                  onClick={handleAddUserShare}
                  className="w-full h-10 bg-[#0176D3] text-white rounded text-[13px] font-bold hover:bg-[#014486] shadow-[0_2px_8px_rgba(0,0,0,0.16)] disabled:opacity-50"
                >
                  {saving ? 'Sharing…' : 'Share'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
